import React, { createContext, useContext, useEffect, useState } from 'react';

interface AHUControlState {
  power: boolean;
  remote: boolean; // Always true
  setpoint: number | undefined;
  mode: 'cool' | 'fan' | 'dry' | null;
  fanSpeed: 'auto' | 'low' | 'med' | 'high' | undefined;
}

interface AHUControlContextType {
  state: AHUControlState;
  setPower: (value: boolean) => void;
  setRemote: (value: boolean) => void;
  setSetpoint: (value: number | undefined) => void;
  setMode: (value: 'cool' | 'fan' | 'dry') => void;
  setFanSpeed: (value: 'auto' | 'low' | 'med' | 'high') => void;
}

const AHUControlContext = createContext<AHUControlContextType | undefined>(undefined);

interface AHUControlProviderProps {
  children: React.ReactNode;
  initialState: {
    status: 'on' | 'off' | 'alarm' | undefined;
    setTemp: number | undefined;
    returnTemp?: number | undefined;
    mode: 'cool' | 'fan' | 'dry' | null;
    opMode: string | undefined;
    fanSpeed: 'auto' | 'low' | 'med' | 'high' | undefined;
  };
}

export function AHUControlProvider({ children, initialState }: AHUControlProviderProps) {
  // Always return true for remote (Operation is always Remote)
  const getRemoteFromOpMode = (_: string | undefined): boolean => {
    return true; // Always Remote
  };

  const [state, setState] = useState<AHUControlState>({
    power: initialState.status === 'on',
    remote: getRemoteFromOpMode(initialState.opMode),
    setpoint: initialState.setTemp,
    mode: initialState.mode,
    fanSpeed: initialState.fanSpeed,
  });

  const setPower = (value: boolean) => setState(prev => ({ ...prev, power: value }));
  const setRemote = (value: boolean) => setState(prev => ({ ...prev, remote: value }));
  const setSetpoint = (value: number | undefined) => setState(prev => ({ ...prev, setpoint: value }));
  const setMode = (value: 'cool' | 'fan' | 'dry') => setState(prev => ({ ...prev, mode: value }));
  const setFanSpeed = (value: 'auto' | 'low' | 'med' | 'high') => setState(prev => ({ ...prev, fanSpeed: value }));

  return (
    <AHUControlContext.Provider value={{
      state,
      setPower,
      setRemote,
      setSetpoint,
      setMode,
      setFanSpeed,
    }}>
      {children}
    </AHUControlContext.Provider>
  );
}

export function useAHUControl() {
  const context = useContext(AHUControlContext);
  if (context === undefined) {
    throw new Error('useAHUControl must be used within an AHUControlProvider');
  }
  return context;
}